import { useMemo } from "react";
import { SearchInput } from "../../common/forms/SearchInput/SearchInput";
import { LucideIcon } from "../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../common/panels/TableView/TableView";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { EntityTableTabs } from "../../common/tables/Tabs/EntityTableTabs";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableColumns,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { MethodFilterBar, MethodFilterForm } from "./MethodFilterBar";
import { NotSet } from "../../common/misc/UIconstants";
import { Method, MethodFilters, MethodFiltersTranslator } from "../../api/Method";
import { TableArrayRenderer } from "../../common/misc/EntityRenders/EntityRenderer";
import { LinkEntity } from "../../common/misc/LinkEntity/LinkEntity";
import { dataFormatsConstants } from "../../api/DataFormats";
import { EntityOrderBy, InferSortByOptions } from "../../api/GenericTypes";

export const switchMethodsDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<MethodFilters>>[] | null | undefined
): SortState<InferSortByOptions<MethodFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "FULL_NAME":
        return { headerId: "default-fullName", sortDirection: sort.sortDirection, property: sort.property };
      default:
        return { headerId: "default-name", sortDirection: "DESC", property: "NAME" };
    }
  });
};
const defaultFilter: MethodFilters = {
  sortBy: [
    {
      property: "NAME",
      sortDirection: "ASC",
    },
  ],
};

export const useMethodsTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"methods">) => {
  const defaults: ColumnsSettings<Method> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-fullName": { pos: 2, active: true, header: fieldLabels.fullName, property: "fullName" },
      "default-description": { pos: 3, active: true, header: fieldLabels.description, property: "description" },
      "default-dataFormats": { pos: 4, active: true, header: fieldLabels.description, property: "dataFormats" },
    }),
    [fieldLabels.description, fieldLabels.fullName, fieldLabels.id, fieldLabels.name]
  );

  const tabStoreDefaults: TableTabsDict<Method, MethodFilters, MethodFilterForm> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultFilter,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    }),
    []
  );

  return { defaults, tabStoreDefaults };
};

export const useMethodsTableColumns: UseEntityTableColumns<"methods"> = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"methods">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<Method> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: 600,
              whiteSpace: "nowrap",
              marginRight: "5px",
            }}
          >
            <LucideIcon name={entityConstants.icon} color="var(--primary)" />
            <span> {row.name}</span>
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: fieldLabels.fullName,
        id: "default-fullName",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: 600,
              whiteSpace: "nowrap",
              marginRight: "5px",
            }}
          >
            {row.fullName || NotSet}
          </div>
        ),
        minWidth: 150,
        width: 400,
        align: "left",
      },
      {
        Header: fieldLabels.description,
        id: "default-description",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: 600,
              whiteSpace: "nowrap",
              marginRight: "5px",
            }}
          >
            {row.description || NotSet}
          </div>
        ),
        minWidth: 150,
        width: 400,
        align: "left",
      },
      {
        Header: fieldLabels.dataFormats,
        id: "default-dataFormats",
        accessor: (row) => (
          <TableArrayRenderer values={row.dataFormats}>
            {(value) => <LinkEntity entityConstants={dataFormatsConstants} property={value} showLink={false} />}
          </TableArrayRenderer>
        ),
        minWidth: 150,
        width: 500,
        align: "left",
      },
    ];
    // Add custom field headers
    return headers;
  }, [
    entityConstants.icon,
    fieldLabels.dataFormats,
    fieldLabels.description,
    fieldLabels.fullName,
    fieldLabels.id,
    fieldLabels.name,
    setSort,
    sort,
  ]);
  return { columns };
};
export const MethodsTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"methods">) => {
  const { defaults, tabStoreDefaults } = useMethodsTableDefaults({ fieldLabels });

  const { resultsCount, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<Method>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<Method, MethodFilters, MethodFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue, onTabChange } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchMethodsDefaultSortState,
    dispatchTabStore,
  });
  const { columns } = useMethodsTableColumns({ fieldLabels, entityConstants, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <MethodFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<Method, MethodFilters>
                filters={filters}
                excludeFilters={{}}
                translatorConsts={MethodFiltersTranslator}
              />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
            </EntityTable.Controls>
            <EntityTable.Body<Method, MethodFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
